// The AWP rule library backend. Mirrors askSmart-backend/src/utils/verity/awpRuleStore.ts.

import { VerityApiError } from "../StandardsLibrary/api";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/verity`;

export type RuleLayer = "contract" | "project" | "company" | "technical" | "baseline";
/** Highest precedence first — the order the library lists them in. */
export const RULE_LAYERS: { key: RuleLayer; label: string }[] = [
  { key: "contract", label: "Contract Requirements" },
  { key: "project", label: "Project Requirements" },
  { key: "company", label: "Company Procedure" },
  { key: "technical", label: "Technical Standards" },
  { key: "baseline", label: "Industry Baseline" },
];

export type RuleSetStatus = "draft" | "active" | "retired";

export interface AwpRuleSet {
  id: string;
  title: string;
  docNo?: string;
  version?: string;
  layer: RuleLayer;
  status: RuleSetStatus;
  ruleCount: number;
  domains: { code: string; name: string; rules: number }[];
  source: { fileName: string; blobName: string; sha1: string };
  problems: string[];
  createdAt: string;
  _etag?: string;
}

export interface AwpRule {
  id: string;
  ruleSetId: string;
  code: string;
  title: string;
  requirement: string;
  domain: string;
  domainCode: string;
  method: string;
  severity: string;
  gate: string;
  evidence: string;
  packageTypes: ("CWP" | "EWP" | "PWP")[];
  order: number;
  /** 1 as imported; above 1 means an administrator has edited it since. */
  version?: number;
  editedAt?: string;
  _etag?: string;
}

export type RuleEdit = Pick<
  AwpRule,
  "title" | "requirement" | "method" | "severity" | "gate" | "evidence" | "packageTypes"
> & { _etag?: string };

export interface CatalogueRead {
  source: { fileName: string; blobName: string; sha1: string };
  metadata: { title: string; docNo: string; version: string; declaredRuleCount?: number };
  ruleCount: number;
  domains: { code: string; name: string; rules: number; declared?: number }[];
  problems: string[];
  sample: AwpRule[];
  /** Set when this exact document is already in the library — a note, not a refusal. */
  duplicateOf?: { id: string; title: string };
  /** Titles already in use. A new standard needs one of its own. */
  takenTitles?: string[];
}

async function request<T>(path: string, init: RequestInit = {}, json = true): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    ...init,
    headers: json ? { "Content-Type": "application/json", ...(init.headers || {}) } : init.headers,
  });
  if (res.status === 204) return undefined as T;
  if (!res.ok) {
    const detail = await res
      .json()
      .then((body) => (body as { error?: string }).error)
      .catch(() => undefined);
    throw new VerityApiError(detail || `Request failed (${res.status})`, res.status);
  }
  return (await res.json()) as T;
}

export async function listRuleSets(): Promise<AwpRuleSet[]> {
  const { items } = await request<{ items: AwpRuleSet[] }>("/awp/rulesets");
  return items || [];
}

export async function listRules(ruleSetId: string): Promise<AwpRule[]> {
  const { items } = await request<{ items: AwpRule[] }>(`/awp/rulesets/${ruleSetId}/rules`);
  return items || [];
}

/** Step 1 — reads the catalogue and reports what it holds. Adds nothing to the library. */
export async function readCatalogue(file: File): Promise<CatalogueRead> {
  const form = new FormData();
  form.append("file", file);
  // No Content-Type header: the browser sets the multipart boundary itself.
  return request<CatalogueRead>("/awp/rulesets/read", { method: "POST", body: form }, false);
}

/** Step 2 — imports the confirmed catalogue as a Draft rule set. */
export async function importRuleSet(input: {
  source: CatalogueRead["source"];
  title: string;
  docNo?: string;
  version?: string;
  layer: RuleLayer;
}): Promise<AwpRuleSet> {
  const { ruleSet } = await request<{ ruleSet: AwpRuleSet }>("/awp/rulesets", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return ruleSet;
}

export interface RuleSetEvent {
  id: string;
  at: string;
  action: string;
  actorName?: string;
  summary: string;
  detail?: string;
}

/** A standard's audit trail, oldest first. */
export async function listRuleSetAudit(id: string): Promise<{ items: RuleSetEvent[]; available: boolean }> {
  const body = await request<{ items: RuleSetEvent[]; available?: boolean }>(`/awp/rulesets/${id}/audit`);
  return { items: body.items || [], available: body.available !== false };
}

/** Title, layer and status. Unlocking an active standard needs `reason`; it goes on the audit trail. */
export async function updateRuleSet(
  id: string,
  patch: Partial<Pick<AwpRuleSet, "title" | "docNo" | "version" | "layer" | "status">> & {
    reason?: string;
    _etag?: string;
  },
): Promise<AwpRuleSet> {
  const { ruleSet } = await request<{ ruleSet: AwpRuleSet }>(`/awp/rulesets/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  return ruleSet;
}

export async function updateRule(ruleSetId: string, ruleId: string, edit: RuleEdit): Promise<AwpRule> {
  const { rule } = await request<{ rule: AwpRule }>(
    `/awp/rulesets/${ruleSetId}/rules/${encodeURIComponent(ruleId)}`,
    { method: "PUT", body: JSON.stringify(edit) },
  );
  return rule;
}

/** Removes one rule; returns the set with its counts brought down to match. */
export async function deleteRule(ruleSetId: string, ruleId: string): Promise<AwpRuleSet> {
  const { ruleSet } = await request<{ ruleSet: AwpRuleSet }>(
    `/awp/rulesets/${ruleSetId}/rules/${encodeURIComponent(ruleId)}`,
    { method: "DELETE" },
  );
  return ruleSet;
}

export async function deleteRuleSet(id: string): Promise<void> {
  await request<void>(`/awp/rulesets/${id}`, { method: "DELETE" });
}
